import { z } from 'zod';
import { phoneSchema } from './lead';
import { tenantSchema } from './valuation';
import { HONEYPOT_FIELD_NAME, honeypotFieldSchema } from './honeypot';
import { attributionSchema } from './attribution';
/**
 * Public Beratung intake (POST /ingest/beratung) — the calculator's step 3
 * ("warm" lead requests a callback). Mirrors what the W1 UI sends; the edge
 * worker validates with this exact schema and forwards it HMAC-signed, the
 * backend re-validates and emits `consult.requested` (flow 3 → E2).
 */
/**
 * Beratungs-widget topics (owner screenshot 26-8, replacing the 21-8 five).
 * Labels live in the UI; the order here mirrors the UI order. `makler` is no
 * longer offered by the widget but stays ACCEPTED on the wire: cached bundles
 * (an open tab from before the redeploy) may still submit it, and the edge
 * rejects out-of-enum topics with a 400 — dropping it would turn that stale
 * tab's submit into a lost lead.
 */
export const BERATUNG_TOPICS = [
    'preisfindung',
    'erbschaft',
    'scheidung',
    'verkauf',
    'vermietung',
    'kaufinteresse',
    'finanzierung',
    'handaenderungssteuer',
    'grundstueckgewinnsteuer',
    'eigenmietwert',
    'grundsteuer',
    'sonstiges',
    // legacy, not in the UI — see the doc comment above.
    'makler',
];
export const beratungTopicSchema = z.enum(BERATUNG_TOPICS);
/**
 * B7 (owner 29-9) — THE WORDS OF A BERATUNGSANFRAGE, ONE LIST FOR EVERY READER.
 *
 * The topic labels are 1:1 what the widget dropdown showed the customer (owner
 * list 26-8); `makler` is the legacy value (see above) with the label the task
 * title always gave it. Until B7 this table lived only in the backend
 * (`consult-side-effects.ts`, the Rückruf task title); the CRM now shows the
 * same topic in the Kontakte list and on the lead page, so it moved HERE and
 * the backend reads it from here — two copies would drift the first time
 * someone renames a topic.
 */
export const BERATUNG_TOPIC_LABELS = {
    preisfindung: 'Preisfindung',
    erbschaft: 'Erbschaft',
    scheidung: 'Scheidung',
    verkauf: 'Verkauf',
    vermietung: 'Vermietung',
    kaufinteresse: 'Kaufinteresse',
    finanzierung: 'Finanzierung',
    handaenderungssteuer: 'Handänderungssteuer',
    grundstueckgewinnsteuer: 'Grundstückgewinnsteuer',
    eigenmietwert: 'Eigenmietwert',
    grundsteuer: 'Grundsteuer',
    sonstiges: 'Sonstiges',
    makler: 'Makler gesucht',
};
/**
 * The surfaces a Beratung can come from (`triggerSource` on the wire). A lead
 * this request MINTS carries the same word as its `source`, which is why the
 * CRM's Quelle filter offers these three next to `LEAD_SOURCES`.
 */
export const BERATUNG_TRIGGER_SOURCES = ['rechner', 'vergleich', 'bewertung'];
/**
 * The channel of a Beratungsanfrage. The intake has NO channel field — every
 * request asks for a call back, and the `consult.requested` event has always
 * said `channel: 'phone'`. The table column carries the word so that a later
 * channel is a new value, not a new column.
 */
export const BERATUNG_KANAELE = ['phone'];
export const BERATUNG_KANAL_LABELS = { phone: 'Telefon' };
export const beratungIngestSchema = z.object({
    /** Cloudflare Turnstile token (verified server-side, like /ingest/lead). */
    turnstileToken: z.string().min(1).max(4096),
    /**
     * Lead id from the preceding calculator submit. Advisory: mock-mode mints
     * non-UUID ids ("lead_…"), so the backend links it only when it is a real,
     * existing lead.
     */
    leadId: z.string().max(80).optional(),
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    email: z.string().trim().toLowerCase().email().max(254),
    phone: phoneSchema,
    address: z.object({
        street: z.string().max(120).optional().default(''),
        zip: z.string().max(10).optional().default(''),
        city: z.string().max(80).optional().default(''),
    }),
    preferredTime: z.string().max(30),
    language: z.enum(['de', 'fr', 'it', 'en']).default('de'),
    /**
     * Which tool/surface triggered this request (W6: 'vergleich' = Zinsvergleich
     * tab; absent = classic Rechner step 3). Zod strips unknown keys, so without
     * this field the worker would silently drop the attribution.
     */
    triggerSource: z.enum(BERATUNG_TRIGGER_SOURCES).optional(),
    /**
     * Which tenant the request belongs to (MLG Verkauf Beratungs-widget sends
     * 'mlg'). Optional for backward compatibility: absent = the platform's
     * default tenant, exactly the pre-existing Rechner/Vergleich behaviour.
     */
    tenant: tenantSchema.optional(),
    /**
     * "Worüber möchten Sie sprechen?" — the Beratungs-widget topic. Drives the
     * Aufgabe title and the advisor notice; free text is deliberately NOT
     * accepted (no PII smuggling into task titles / push payloads).
     */
    topic: beratungTopicSchema.optional(),
    /**
     * Widget contact consent ("telefonisch und per E-Mail kontaktiert zu werden
     * und Informationen zugesendet zu bekommen") — persisted as a
     * marketing_email consent record + lead.marketingConsent, next to the
     * privacy_policy record `consentVersion` produces.
     */
    marketingConsent: z.boolean().optional(),
    /**
     * Version of the consent text the user ticked (W6b Vergleich Beratung form —
     * unlike Rechner step 3 there is no preceding lead submit that recorded
     * consent). Same const family as the lead gate's `consent.textVersion`;
     * when present the backend persists a privacy_policy consent_record.
     */
    consentVersion: z.string().trim().min(1).max(64).optional(),
    /**
     * Version of the MARKETING opt-in text, separate from `consentVersion`
     * (which versions the privacy text). The two are versioned independently by
     * design — bundling them would stamp a marketing grant with the version of a
     * document the visitor consented to for a different purpose, which is exactly
     * what a revDSG consent record has to keep apart. Falls back to
     * `consentVersion` when absent, so older clients keep working.
     */
    marketingConsentVersion: z.string().trim().min(1).max(64).optional(),
    /**
     * Campaign attribution (utm_* + Google click ids), like the lead and
     * valuation intakes. Stored only on a lead this request MINTS — an existing
     * lead keeps the attribution of the visit that created it.
     */
    utm: attributionSchema,
    /** Honeypot (Fase A6) — same contract, same computed key, as the valuation submit. */
    [HONEYPOT_FIELD_NAME]: honeypotFieldSchema,
});
